import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { useGetOneEventCategory } from "#src/components/hooks/adapters/useEventCategories";
import { useListEvents } from "#src/components/hooks/adapters/useEvents";
import { useGetOneEventSchema } from "#src/components/hooks/adapters/useEventSchemas";
import { linkToEventCategoryDetail } from "#src/routes/events/event-categories/[eventCategoryId]";
import { linkToEventTemplateDetail } from "#src/routes/events/event-categories/[eventCategoryId]/templates/[templateId]/details";
import { linkToEventDetails } from "#src/routes/events/event-categories/[eventCategoryId]/templates/[templateId]/events/[eventId]";
import { useQuery } from "@tanstack/react-query";
import {
  Accordion,
  KeyValueList,
  KeyValuePairList,
  Pill,
  PillProps,
  Skeleton,
} from "@validereinc/common-components";
import {
  EventCategoryType,
  EventSchemaType,
  EventStatusType,
  EventType,
  UsersAdapter,
  WorkflowTaskType,
  WorkflowType,
} from "@validereinc/domain";
import { datetimeFormatter, toTitleCaseString } from "@validereinc/utilities";
import classNames from "classnames/bind";
import React from "react";
import styles from "./WorkflowTaskCompleteEvent.module.scss";

const cx = classNames.bind(styles);

const { AccordionPanel } = Accordion;

type WorkflowTaskCompleteEventWithTaskProps = {
  workflowTask?: WorkflowTaskType;
  workflow?: WorkflowType;
  eventSchemaId?: string;
  shouldOnlyShowEventSchemaAndCategory?: false;
};

type WorkflowTaskCompleteEventSchemaAndCategoryOnlyProps = {
  eventSchemaId?: string;
  shouldOnlyShowEventSchemaAndCategory: true;
};

export const WorkflowTaskCompleteEvent = (
  props:
    | WorkflowTaskCompleteEventWithTaskProps
    | WorkflowTaskCompleteEventSchemaAndCategoryOnlyProps
) => {
  const { eventSchemaId, shouldOnlyShowEventSchemaAndCategory } = props;

  const eventIds =
    ("workflowTask" in props
      ? props.workflowTask?.emission_event?.map((event) => event.id)
      : []) ?? [];
  const eventsQuery = useListEvents(
    {
      filters: {
        id: eventIds,
      },
    },
    {
      enabled: eventIds.length > 0,
    }
  );

  const events = eventsQuery.data?.data ?? [];
  const isEventsLoading = eventIds.length && eventsQuery.isLoading;

  const eventSchemaQuery = useGetOneEventSchema(
    { id: eventSchemaId ?? "" },
    { enabled: Boolean(eventSchemaId) }
  );
  const eventSchema = eventSchemaQuery.data?.data;
  const isEventSchemaLoading = eventSchemaQuery.isLoading;

  const eventCategoryQuery = useGetOneEventCategory(
    { id: eventSchema?.event_category_id ?? "" },
    { enabled: Boolean(eventSchema?.event_category_id) }
  );
  const eventCategory = eventCategoryQuery.data?.data;
  const isEventCategoryLoading = eventCategoryQuery.isLoading;

  const isSchemaOrCategoryLoading =
    isEventSchemaLoading || isEventCategoryLoading;
  const isLoading = isEventsLoading || isSchemaOrCategoryLoading;

  return (
    <Accordion
      key={"event"}
      defaultActiveKeys={["event"]}
    >
      <AccordionPanel
        dataKey={"event"}
        title={"Event"}
      >
        <>
          {events?.map((data) => (
            <Skeleton
              key={data?.id}
              isLoading={isLoading}
              style={{ minHeight: 200 }}
            >
              {!shouldOnlyShowEventSchemaAndCategory &&
                data &&
                props.workflow &&
                props.workflowTask && (
                  <EventDetails
                    event={data}
                    workflowTaskId={props.workflowTask?.id}
                    eventSchema={eventSchema}
                    eventCategory={eventCategory}
                  />
                )}
            </Skeleton>
          ))}

          {shouldOnlyShowEventSchemaAndCategory && (
            <Skeleton
              isLoading={isSchemaOrCategoryLoading}
              style={{ minHeight: 200 }}
            >
              {!isSchemaOrCategoryLoading && (
                <EventDetails
                  eventSchema={eventSchema}
                  eventCategory={eventCategory}
                />
              )}
            </Skeleton>
          )}
        </>
      </AccordionPanel>
    </Accordion>
  );
};

const EventDetails = ({
  event,
  workflowTaskId,
  eventSchema,
  eventCategory,
}: {
  event?: EventType;
  workflowTaskId?: string;
  eventSchema?: EventSchemaType;
  eventCategory?: EventCategoryType;
}) => {
  const { data: createdByUser } = useQuery(
    ["users", event?.created_by],
    () => {
      if (!event?.created_by) return;
      return UsersAdapter.getOne({ id: event.created_by });
    },
    {
      enabled: Boolean(event?.created_by),
      staleTime: 3 * 60 * 1000,
      select: (resp) => resp?.data,
    }
  );

  const data: KeyValuePairList = [];

  if (event) {
    data.push({
      title: "Event",
      value:
        eventSchema && workflowTaskId ? (
          <RoutingLink
            to={{
              pathname: linkToEventDetails(
                eventCategory?.id,
                eventSchema?.id,
                event.id
              ),
              search: `?workflowTaskId=${workflowTaskId}`,
            }}
          >
            {event.name}
          </RoutingLink>
        ) : (
          "-"
        ),
    });
    if (event.description) {
      data.push({
        title: "Description",
        value: event.description,
      });
    }

    data.push({
      title: "Status",
      value: (
        <Pill
          variant={
            (
              {
                open: "warning",
                overdue: "error",
                dismissed: "default",
                resolved: "success",
              } satisfies Record<EventStatusType, PillProps["variant"]>
            )[event.status]
          }
        >
          {toTitleCaseString(event.status, {
            intent: "ui",
            exceptions: ["oil-and-gas"],
          })}
        </Pill>
      ),
    });
  }

  data.push({
    title: "Template",
    value: eventSchema ? (
      <RoutingLink
        to={linkToEventTemplateDetail(
          eventSchema.event_category_id,
          eventSchema.id
        )}
      >
        {eventSchema.name}
      </RoutingLink>
    ) : (
      "-"
    ),
  });

  data.push({
    title: "Category",
    value: eventCategory ? (
      <RoutingLink to={linkToEventCategoryDetail(eventCategory.id)}>
        {eventCategory.name}
      </RoutingLink>
    ) : (
      "-"
    ),
  });

  if (createdByUser) {
    data.push({
      title: "Created By",
      value: createdByUser?.name ?? "-",
    });
  }

  if (event) {
    data.push({
      title: "Created At",
      value: datetimeFormatter(new Date(event.created_at)),
    });
  }

  return (
    <KeyValueList
      className={cx("keyValueListContainer")}
      data={data}
    />
  );
};
